import React, { useRef } from "react";
import L from "leaflet";
import { CircleMarker, Popup, useMapEvents } from "react-leaflet";

// One shared canvas renderer — ~2000 SVG circle nodes make pan/zoom choppy;
// canvas draws them in a single element.
const canvasRenderer = L.canvas({ padding: 0.5 });

const PALETTE = {
  high: "#FF3B1F",
  nominal: "#FF8A1F",
  low: "#FFC74A",
};

// FRP (Fire Radiative Power, megawatts) → marker radius. A real wildfire
// (50+ MW) is unmistakably larger than a small heat anomaly (~1 MW).
function frpRadius(frp) {
  if (frp == null) return 6;
  if (frp >= 100) return 22;
  if (frp >= 50) return 18;
  if (frp >= 20) return 14;
  if (frp >= 10) return 11;
  if (frp >= 5) return 9;
  if (frp >= 1) return 7;
  return 5;
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function markerRadius(h, frpHeatmap) {
  const isModis = (h.source || "").startsWith("MODIS");
  return frpHeatmap ? frpRadius(h.frp) : isModis ? 6 : h.confidence === "high" ? 5 : 4;
}

function ViirsHotspotsLayer({ hotspots = [], frpHeatmap = false, colorByAge = false }) {
  const now = Date.now();
  const refs = useRef({});
  // useMapEvents registers handlers once (first render), so read the
  // hotspot list through refs — the prop is empty until data loads.
  const hotspotsRef = useRef(hotspots);
  hotspotsRef.current = hotspots;
  const frpRef = useRef(frpHeatmap);
  frpRef.current = frpHeatmap;
  // Perimeter/evac SVG polygons sit above the hotspot canvas and swallow
  // clicks (hotspots are usually INSIDE perimeters). Hit-test hotspots on
  // every map click and open the dot's popup — it opens after the polygon's,
  // so the hotspot wins when you actually clicked a dot.
  const map = useMapEvents({
    click(e) {
      const pt = map.latLngToContainerPoint(e.latlng);
      let best = null;
      for (const h of hotspotsRef.current) {
        const hp = map.latLngToContainerPoint([h.latitude, h.longitude]);
        const dx = hp.x - pt.x;
        const dy = hp.y - pt.y;
        const d2 = dx * dx + dy * dy;
        const r = Math.max(markerRadius(h, frpRef.current) + 4, 9);
        if (d2 <= r * r && (!best || d2 < best.d2)) best = { id: h.id, d2 };
      }
      if (best) setTimeout(() => refs.current[best.id]?.openPopup(), 0);
    },
  });
  return hotspots.map((h) => {
    let color = PALETTE[h.confidence] || PALETTE.nominal;
    let fillOpacity = frpHeatmap ? 0.55 : 0.9;
    if (colorByAge && h.acq_datetime) {
      const ageH = (now - new Date(h.acq_datetime).getTime()) / 36e5;
      if (ageH <= 6) { color = "#FF2A12"; fillOpacity = 1; }
      else if (ageH <= 12) { color = "#FF6A1F"; fillOpacity = 0.85; }
      else if (ageH <= 24) { color = "#E09A4A"; fillOpacity = 0.6; }
      else { color = "#8A7A66"; fillOpacity = 0.4; }
    }
    const isModis = (h.source || "").startsWith("MODIS");
    const r = markerRadius(h, frpHeatmap);
    return (
      <CircleMarker
        key={`viirs-${h.id}`}
        ref={(m) => { refs.current[h.id] = m; }}
        center={[h.latitude, h.longitude]}
        radius={r}
        renderer={canvasRenderer}
        pathOptions={{
          color: isModis ? "#7AD7FF" : "#0A0D0C",
          weight: isModis ? 1.2 : frpHeatmap ? 0.4 : 0.6,
          fillColor: color,
          fillOpacity,
        }}
      >
        <Popup>
          <div className="min-w-[200px]" data-testid={`viirs-popup-${h.id}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color }}>
              {isModis ? "MODIS" : "VIIRS"} · Satellite hotspot
            </div>
            <div className="text-[14px] text-tac-dim font-mono-tac tabular space-y-0.5">
              <div>{h.latitude.toFixed(4)}, {h.longitude.toFixed(4)}</div>
              <div>Detected {fmtDate(h.acq_datetime)} UTC</div>
              <div>
                Confidence: <span className="text-tac-text font-bold">{h.confidence}</span>
                {h.daynight && ` · ${h.daynight === "D" ? "Day" : "Night"}`}
              </div>
              {h.frp != null && <div>FRP: {h.frp.toFixed(1)} MW</div>}
              {h.bright_ti4 != null && <div>Brightness: {h.bright_ti4.toFixed(1)} K</div>}
              {h.satellite && (
                <div>
                  Satellite: {isModis ? h.satellite : `NOAA-${h.satellite}`}
                  {isModis ? " (1 km px)" : " (375 m px)"}
                </div>
              )}
            </div>
            <div className="text-[10px] font-mono-tac text-tac-faint mt-2 pt-1 border-t border-tac-border/60">
              Source: NASA FIRMS · {h.source}
            </div>
          </div>
        </Popup>
      </CircleMarker>
    );
  });
}

export default React.memo(ViirsHotspotsLayer);
